import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomInput from "../formik/CustomInput.tsx";
import CustomTextArea from "../formik/CustomTextArea.tsx";
import {
  saveDeductionTemplate,
  updateDeductionTemplate,
  DeductionTemplate,
} from "../../services/deductions.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import Loader from "../Loader.tsx";

interface TemplateFormProps {
  close: () => void;
  onSuccess: () => void;
  templateToEdit?: DeductionTemplate | null;
  isEditing?: boolean;
}

interface TemplateFormValues {
  name: string;
  description: string;
  amount: number;
}

const TemplateForm: React.FC<TemplateFormProps> = ({
  close,
  onSuccess,
  templateToEdit,
  isEditing = false,
}) => {
  const [loading, setLoading] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("El nombre es requerido")
      .min(2, "El nombre debe tener al menos 2 caracteres")
      .max(100, "El nombre no puede exceder 100 caracteres"),
    description: Yup.string()
      .required("La descripción es requerida")
      .min(5, "La descripción debe tener al menos 5 caracteres")
      .max(500, "La descripción no puede exceder 500 caracteres"),
    amount: Yup.number()
      .required("El monto es requerido")
      .min(0.01, "El monto debe ser mayor a 0")
      .max(999999.99, "El monto no puede exceder 999,999.99"),
  });

  const formik = useFormik<TemplateFormValues>({
    initialValues: {
      name: templateToEdit?.name || "",
      description: templateToEdit?.description || "",
      amount: templateToEdit?.amount || 0,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);

      try {
        const payload = {
          name: values.name,
          description: values.description,
          amount: Number(values.amount),
        };

        if (isEditing && templateToEdit) {
          await updateDeductionTemplate(templateToEdit.id, payload);
          notification("", "Plantilla actualizada exitosamente", "success");
        } else {
          await saveDeductionTemplate(payload);
          notification("", "Plantilla creada exitosamente", "success");
        }

        onSuccess();
      } catch (error) {
        notification(
          "Error ⚠️",
          `Error al ${isEditing ? "actualizar" : "crear"} la plantilla`,
          "danger"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <React.Fragment>
      <Loader isLoading={loading} />

      <div className="mb-4">
        <div className="alert alert-info" role="alert">
          <i className="fa fa-info-circle me-2"></i>
          <strong>Información:</strong> Las plantillas te permiten guardar
          configuraciones frecuentes de deducciones para reutilizarlas más
          tarde.
        </div>
      </div>

      <CustomInput
        formik={formik}
        field="name"
        label="Nombre"
        placeholder="Ej: Descuento por préstamo, Uniforme, etc."
        required
        sm={9}
        smLabel={3}
      />

      <CustomTextArea
        field="description"
        formik={formik}
        label="Descripción"
        placeholder="Describe el propósito de esta plantilla..."
        smLabel={3}
        sm={9}
        required
      />
      <CustomInput
        classInput="mt-4"
        formik={formik}
        field="amount"
        label="Monto"
        type="number"
        placeholder="$100.00"
        required
        sm={9}
        smLabel={3}
      />

      <div className="mt-4 p-3 bg-light rounded">
        <h6 className="mb-2">
          <i className="fa fa-lightbulb me-2"></i>
          Consejos para crear plantillas efectivas:
        </h6>
        <ul className="mb-0 small text-muted">
          <li>Usa nombres descriptivos y claros</li>
          <li>Incluye detalles específicos en la descripción</li>
          <li>Establece montos que uses frecuentemente</li>
          <li>Revisa y actualiza tus plantillas periódicamente</li>
        </ul>
      </div>

      <div className="d-flex justify-content-end mt-4">
        <button
          className="btn btn-secondary me-2"
          onClick={close}
          disabled={loading}
        >
          Cancelar
        </button>
        <button
          className="btn btn-success"
          onClick={() => formik.handleSubmit()}
          disabled={loading}
        >
          {isEditing ? "Actualizar" : "Guardar"} Plantilla
        </button>
      </div>
    </React.Fragment>
  );
};

export default TemplateForm;
